import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type UIEvent,
} from 'react';
import {
  Combobox,
  Loader,
  Pill,
  PillsInput,
  ScrollArea,
  useCombobox,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { FormField } from '../FormField';
import { Button } from '../../atoms/Button';
import { Group } from '../../atoms/Group';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import type { AsyncSearchPageResult } from '../AsyncSearchSelect/AsyncSearchSelect';

export interface AsyncMultiSearchSelectProps extends CoreProps {
  label?: string;
  placeholder?: string;
  values: string[];
  /** Labels for selected values (value → label). */
  valueLabels?: Record<string, string>;
  onChange: (values: string[], labels: Record<string, string>) => void;
  fetchPage: (args: {
    search: string;
    page: number;
    pageLimit: number;
  }) => Promise<AsyncSearchPageResult>;
  pageLimit?: number;
  debounceMs?: number;
  error?: string;
  disabled?: boolean;
}

export function AsyncMultiSearchSelect({
  label,
  placeholder = 'Search to add…',
  values,
  valueLabels = {},
  onChange,
  fetchPage,
  pageLimit = 20,
  debounceMs = 300,
  error,
  disabled,
  testId,
  view = 'all',
}: AsyncMultiSearchSelectProps) {
  const shouldRender = useShouldRender(view);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => {
      void loadPage(1, '', false);
    },
  });

  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const hasMore = options.length < total;
  const selectedSet = new Set(values);

  const loadPage = useCallback(
    async (nextPage: number, nextSearch: string, append: boolean) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setFetchError('');
      try {
        const result = await fetchPageRef.current({
          search: nextSearch,
          page: nextPage,
          pageLimit,
        });
        if (id !== requestId.current) return;
        const rows = Array.isArray(result.rows) ? result.rows : [];
        setTotal(Number(result.total) || 0);
        setPage(nextPage);
        setOptions((prev) => {
          if (!append) return rows;
          const seen = new Set(prev.map((p) => p.value));
          return [...prev, ...rows.filter((r) => !seen.has(r.value))];
        });
      } catch (e) {
        if (id !== requestId.current) return;
        setFetchError(e instanceof Error ? e.message : 'Failed to load options');
        if (!append) setOptions([]);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageLimit],
  );

  function handleSearchChange(q: string) {
    setSearch(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void loadPage(1, q.trim(), false);
    }, debounceMs);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    if (!hasMore || loadingMore || loading) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) void loadPage(page + 1, search.trim(), true);
  }

  function addOption(optionValue: string) {
    if (selectedSet.has(optionValue)) return;
    const opt = options.find((o) => o.value === optionValue);
    const nextLabels = { ...valueLabels };
    if (opt) nextLabels[optionValue] = opt.label;
    onChange([...values, optionValue], nextLabels);
    setSearch('');
  }

  function removeValue(optionValue: string) {
    const nextLabels = { ...valueLabels };
    delete nextLabels[optionValue];
    onChange(
      values.filter((v) => v !== optionValue),
      nextLabels,
    );
  }

  if (!shouldRender) return null;

  return (
    <FormField label={label} error={error || fetchError || undefined}>
      <Combobox store={combobox} withinPortal zIndex={400} onOptionSubmit={addOption}>
        <Combobox.DropdownTarget>
          <PillsInput
            data-testid={testId}
            pointer
            disabled={disabled}
            onClick={() => combobox.openDropdown()}
            rightSection={
              loading && combobox.dropdownOpened ? (
                <Loader size={14} />
              ) : (
                <IconChevronDown size={14} />
              )
            }
          >
            <Pill.Group>
              {values.map((val) => (
                <Pill
                  key={val}
                  withRemoveButton
                  onRemove={() => removeValue(val)}
                >
                  {valueLabels[val] || val}
                </Pill>
              ))}
              <Combobox.EventsTarget>
                <PillsInput.Field
                  placeholder={values.length === 0 ? placeholder : undefined}
                  value={search}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleSearchChange(e.currentTarget.value)
                  }
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize
              mah={240}
              type="scroll"
              viewportProps={{
                onScroll: (e: UIEvent<HTMLDivElement>) => handleScroll(e),
              }}
            >
              {options.filter((o) => !selectedSet.has(o.value)).length === 0 && !loading ? (
                <Combobox.Empty>No results</Combobox.Empty>
              ) : (
                options
                  .filter((o) => !selectedSet.has(o.value))
                  .map((item) => (
                    <Combobox.Option value={item.value} key={item.value}>
                      {item.label}
                    </Combobox.Option>
                  ))
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
          {(hasMore || loadingMore) && (
            <Combobox.Footer>
              <Group justify="center" py={4}>
                <Button
                  size="xs"
                  variant="subtle"
                  loading={loadingMore}
                  onClick={() => void loadPage(page + 1, search.trim(), true)}
                >
                  {loadingMore
                    ? 'Loading…'
                    : `Load more (${options.length}/${total})`}
                </Button>
              </Group>
            </Combobox.Footer>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </FormField>
  );
}
